import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle, Package } from "@phosphor-icons/react";

function receiptMap(po) {
  return (po?.grns || []).reduce((totals, grn) => {
    (grn.entries || []).forEach((entry) => {
      totals[entry.item_id] = (totals[entry.item_id] || 0) + Number(entry.qty_received || 0);
    });
    return totals;
  }, {});
}

export default function MaterialReceiptDialog({
  po,
  open,
  onOpenChange,
  onRecorded,
  testIdPrefix = "material-receipt",
}) {
  const [quantities, setQuantities] = useState({});
  const [note, setNote] = useState("");
  const [receiptDate, setReceiptDate] = useState("");
  const [saving, setSaving] = useState(false);
  const received = useMemo(() => receiptMap(po), [po]);

  useEffect(() => {
    if (!open) return;
    setQuantities({});
    setNote("");
    setReceiptDate(new Date().toISOString().slice(0, 10));
  }, [open, po?.id]);

  const saveReceipt = async () => {
    const entries = Object.entries(quantities)
      .map(([item_id, qty_received]) => ({
        item_id,
        qty_received: Number(qty_received) || 0,
      }))
      .filter((entry) => entry.qty_received > 0);

    if (!entries.length) {
      toast.error("Enter at least one received quantity");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/grn`, {
        entries,
        note,
        grn_date: receiptDate,
      });
      toast.success(`${data.po_no} receipt recorded`);
      onOpenChange(false);
      onRecorded?.(data);
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail) || "Could not record this receipt");
    } finally {
      setSaving(false);
    }
  };

  const lines = (po?.items || []).filter((item) => Number(item.qty) > 0);
  const orderedTotal = lines.reduce((total, item) => total + Number(item.qty || 0), 0);
  const receivedTotal = lines.reduce(
    (total, item) => total + Math.min(received[item.id] || 0, Number(item.qty || 0)),
    0,
  );
  const remainingTotal = Math.max(0, orderedTotal - receivedTotal);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl border-slate-200 bg-white text-slate-900"
        data-testid={`${testIdPrefix}-dialog`}
      >
        <DialogHeader className="border-b border-slate-200 pb-3">
          <DialogTitle className="font-heading flex items-center gap-2 text-lg font-extrabold">
            <Package size={20} className="text-emerald-700" />
            Material Receipt — {po?.po_no}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Record only this delivery. Received and balance quantities remain cumulative across all
            GRNs, and the PO completes automatically when every line is fully received.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[62vh] space-y-4 overflow-y-auto pr-1">
          <div
            className="grid grid-cols-3 divide-x divide-emerald-100 border border-emerald-200 bg-emerald-50"
            data-testid={`${testIdPrefix}-totals`}
          >
            <Metric label="Ordered" value={orderedTotal} />
            <Metric label="Received" value={receivedTotal} tone="emerald" />
            <Metric label="Balance" value={remainingTotal} tone="amber" />
          </div>

          {!lines.length ? (
            <p
              className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
              data-testid={`${testIdPrefix}-no-lines`}
            >
              This PO has no quantity-based lines to receive.
            </p>
          ) : (
            <div className="overflow-x-auto border border-slate-200">
              <table className="w-full min-w-[660px] text-sm">
                <thead className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2 text-right">Ordered</th>
                    <th className="px-3 py-2 text-right">Received</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2 text-right">Receive now</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lines.map((item) => {
                    const ordered = Number(item.qty || 0);
                    const got = Math.min(received[item.id] || 0, ordered);
                    const remaining = Math.max(0, ordered - got);
                    return (
                      <tr key={item.id} data-testid={`${testIdPrefix}-line-${item.id}`}>
                        <td className="px-3 py-2 text-slate-800">
                          {item.item || "Unnamed item"}
                          {item.specification && (
                            <div className="mt-0.5 text-[10px] text-slate-500">
                              {item.specification}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-600">{ordered}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{got}</td>
                        <td className="px-3 py-2 text-right font-mono text-amber-700">{remaining}</td>
                        <td className="px-3 py-2 text-right">
                          <Input
                            type="number"
                            min="0"
                            max={remaining}
                            step="any"
                            disabled={!remaining}
                            value={quantities[item.id] ?? ""}
                            placeholder={remaining ? "0" : "Complete"}
                            onChange={(event) =>
                              setQuantities((current) => ({
                                ...current,
                                [item.id]: event.target.value,
                              }))
                            }
                            className="ml-auto h-8 w-28 border-slate-300 text-right font-mono"
                            data-testid={`${testIdPrefix}-qty-${item.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-[155px_1fr]">
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-600">
                Receipt date
              </span>
              <Input
                type="date"
                value={receiptDate}
                onChange={(event) => setReceiptDate(event.target.value)}
                className="border-slate-300"
                data-testid={`${testIdPrefix}-date`}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-600">
                Challan / inspection note
              </span>
              <Input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Challan number, transporter, inspection remark…"
                className="border-slate-300"
                data-testid={`${testIdPrefix}-note`}
              />
            </label>
          </div>

          {(po?.grns || []).length > 0 && (
            <div data-testid={`${testIdPrefix}-history`}>
              <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Receipt history
              </div>
              <div className="space-y-2">
                {[...(po.grns || [])].reverse().map((grn) => (
                  <div
                    key={grn.grn_no}
                    className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
                    data-testid={`${testIdPrefix}-history-${grn.grn_no}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-1">
                      <span className="font-mono font-bold text-emerald-700">
                        {grn.grn_no} · {grn.date}
                      </span>
                      <span className="text-slate-500">Recorded by {grn.received_by}</span>
                    </div>
                    <div className="mt-1 text-slate-700">
                      {(grn.entries || [])
                        .map((entry) => `${entry.item}: ${entry.qty_received}`)
                        .join(" · ")}
                    </div>
                    {grn.note && <div className="mt-1 italic text-slate-500">{grn.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-300"
            data-testid={`${testIdPrefix}-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={saveReceipt}
            disabled={saving || !lines.length}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid={`${testIdPrefix}-save`}
          >
            <CheckCircle size={15} weight="bold" />
            {saving ? "Recording…" : "Record receipt"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, tone = "slate" }) {
  const toneClass = {
    slate: "text-slate-800",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
  }[tone];

  return (
    <div className="px-3 py-2 text-center">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-mono text-base font-extrabold ${toneClass}`}>{value}</div>
    </div>
  );
}